import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { PlayerSearch } from '../Form';
import Img from '../Img';
import { PartnerSelectionChoices } from '../../utils/constants';
import { getPartnerSearchParams } from '../../utils/events';
import { teamPaymentTypeIds, regStepIds, registrationStateTypeIds } from '../../utils/eventConstants';
import { isValidPlayerName } from '../../utils';
import { getNeedsPartnerPlayers } from '../../actions/event-profile-actions';
import { eventShape, userShape, wizardStepShape, playerShape } from '../../propTypes';

import styles from './eventRegForm.scss';
import FindPlayerFromNeedPartner from './FindPlayerFromNeedPartner';

const PartnerSelectionV2 = (props) => {
    const {
        event,
        user,
        addPartnerError,
        teammate,
        selectedPlayer,
        sidebarFlow,
        selectedNeedsPartnerPlayers = {},
        isNeedPartnerSelectionEnabled = false,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const eventProfile = useSelector(({ eventProfile }) => eventProfile);
    const dispatch = useDispatch();

    const { types: { tms: { genderTypes } } } = coreUI;
    const { needsPartnerPlayers = [] } = eventProfile.needsPartnerPlayers?.data || {};
    const selectedDivision = event?.eventDivisions.find(div => div.id === props.step?.divisionId);
    const selectedMembersWithDivisionId = Object.values(selectedNeedsPartnerPlayers);

    const isAddChangePartnerAllowed = event?.changePartnerAllowed;
    const showNeedPartnerList = (isAddChangePartnerAllowed ||
        ((selectedDivision?.registerWithoutPartner || isNeedPartnerSelectionEnabled) &&
            selectedDivision?.teamPaymentTypeId === teamPaymentTypeIds.PAY_SEPARATELY)) &&
        !!needsPartnerPlayers.length;

    useEffect(() => {
        if (props.step?.divisionId) {
            dispatch(getNeedsPartnerPlayers({ eventId: event.id, divisionId: props.step.divisionId }));
        }
    }, []);

    const hideNeedPartnerOption = sidebarFlow === regStepIds.CHANGE_PARTNER && props.step?.playerIsInNeedPartnerList;
    const isPreRegAndEligibleList = event.registrationStateId === registrationStateTypeIds.PRE_REGISTRATION && event.hasEligiblePlayersList;

    // Search player list filtering for eligible list events
    let onlyShowEligiblePlayersInSearch = false;
    if (event.hasEligiblePlayersList) {
        onlyShowEligiblePlayersInSearch = true;
        if (event.registrationStateId !== registrationStateTypeIds.PRE_REGISTRATION && selectedDivision?.excludeFromEligibleListRestrictions === true) {
            onlyShowEligiblePlayersInSearch = false;
        }
    }

    if (teammate === PartnerSelectionChoices.INVITE) {
        return (
            <div>
                <div className="text-dark fs14 text-medium" data-testid="event-reg-form.partner-selection-v2.invite-title">Invite your Partner to UTR Sports</div>
                {props.step.invite === 'invite' && (
                    <div className="mt8" data-testid="event-reg-form.partner-selection-v2.invite-form-wrapper">
                        <div className={`${styles.partnerEmailDescription} text-small mb16`} data-testid="event-reg-form.partner-selection-v2.invite-description">
                            Your partner will receive an invitation email to register. They must create an account on UTR Sports using the provided link in order to complete their registration.
                        </div>
                        <div className={`form-group mb8`}>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    name="inviteName"
                                    placeholder="Enter partner first name and last name"
                                    className="form-control"
                                    value={props.step.inviteName}
                                    onChange={(e) => props.handleInviteChange(e)}
                                    data-testid="event-reg-form.partner-selection-v2.invite-name-input"
                                />
                                <div className="error-indicator" />
                            </div>
                        </div>
                        <div className={`form-group mb8 ${(props.step.errors.inviteEmail) && 'is-invalid'}`}>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    name="inviteEmail"
                                    placeholder="Enter partner email address"
                                    className="form-control"
                                    value={props.step.inviteEmail}
                                    onChange={(e) => props.handleInviteChange(e)}
                                    data-testid="event-reg-form.partner-selection-v2.invite-email-input"
                                />
                                <div className="error-indicator" />
                            </div>
                            {props.step.errors.inviteEmail && <div className="invalid-feedback">{props.step.errors.inviteEmail}</div>}
                        </div>
                    </div>
                )}
                {props.step.invite === PartnerSelectionChoices.INVITE && (
                    <button
                        type="button"
                        className="mb24 btn btn-primary btn-xl btn-block text-uppercase"
                        onClick={props.addTeammateToDivision}
                        disabled={!(isValidPlayerName(props.step.inviteName) && props.step.inviteEmail)}
                        data-testid="event-reg-form.partner-selection-v2.invite-partner-btn"
                    >
                        Invite Partner
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="row">
            <div className="col">
                {!props.step.selectedPlayer && (
                    <div>
                        <div className={`radio-button noselect`} data-testid="event-reg-form.partner-selection-v2.existing-partner-option">
                            <input
                                type="radio"
                                id={PartnerSelectionChoices.EXISTING}
                                name="teammate"
                                value={PartnerSelectionChoices.EXISTING}
                                checked={teammate === PartnerSelectionChoices.EXISTING}
                                onChange={props.addTeammate}
                                data-testid="event-reg-form.partner-selection-v2.existing-radio"
                            />
                            <label htmlFor={PartnerSelectionChoices.EXISTING} className="flex-wrap" data-testid="event-reg-form.partner-selection-v2.existing-partner-label">
                                <div className="radio-button-check" data-testid="event-reg-form.partner-selection-v2.existing-check-icon"><Img src="/img/icons/checkmark-icon.svg" /></div>
                                <div className="radio-button-label flex-1 mw0" style={{ margin: '16px 0' }}>
                                    <div data-testid="event-reg-form.partner-selection-v2.add-partner-title">Add a partner</div>
                                    <div className="mt8">
                                        <div className={`${styles.partnerEmailDescription} text-small mb16`}>
                                            {isPreRegAndEligibleList ? `Enter your partner's name. Only players with a claimed profile and marked eligible by the event provider will show up.` : 'Search for players who have a UTR Sports account and are either not registered for this event or are already on an eligible Need Partner list.'}
                                        </div>
                                    </div>
                                    {props.step.invite === PartnerSelectionChoices.EXISTING && (
                                        <PlayerSearch
                                            className="form-control"
                                            handlePlayerSelect={props.selectTeammate}
                                            placeholder="Search by name"
                                            showInvitePlayer={sidebarFlow !== regStepIds.CHANGE_PARTNER}
                                            excludeProPlayers={!user.isPro}
                                            eventId={event?.id}
                                            excludeNonEligiblePlayers={onlyShowEligiblePlayersInSearch}
                                            eventProviderEmail={event?.eventDirectors[0]?.memberEmail}
                                            sportTypeId={event.sportTypeId}
                                            clearAfterSelect
                                            searchParams={getPartnerSearchParams(selectedDivision, user, genderTypes)}
                                            claimed={true}
                                            searchMembersOnly={true}
                                            themeV3
                                            data-testid="event-reg.partner-search"
                                        />
                                    )}
                                    {props.step.invite === PartnerSelectionChoices.EXISTING && (
                                        <button
                                            type="button"
                                            className={`btn btn-primary btn-block text-uppercase`}
                                            onClick={props.addTeammateToDivision}
                                            disabled={!props.step.selectedPlayer || (props.step.selectedPlayer.profileId <= 0 && !props.step.selectedPlayer.email)}
                                            data-testid="event-reg-form.partner-selection-v2.add-partner-search-btn"
                                        >
                                            Add Partner
                                        </button>
                                    )}
                                </div>
                            </label>
                        </div>
                        {showNeedPartnerList && (
                            <FindPlayerFromNeedPartner
                                {...props}
                                teammate={teammate}
                                needsPartnerPlayers={needsPartnerPlayers.filter(player => !selectedMembersWithDivisionId.includes(`${player.id}-${player.divisionId}`))}
                                sportTypeId={event.sportTypeId}
                                selectedDivision={selectedDivision}
                                utrTypeId={event.utrTypeId}
                            />
                        )}
                        {selectedDivision?.registerWithoutPartner && selectedDivision?.teamPaymentTypeId === teamPaymentTypeIds.PAY_SEPARATELY && !hideNeedPartnerOption && (
                            <div className={`radio-button noselect`} data-testid="event-reg-form.partner-selection-v2.needs-partner-option">
                                <input
                                    type="radio"
                                    id={PartnerSelectionChoices.NEEDS_PARTNER}
                                    name="teammate"
                                    value={PartnerSelectionChoices.NEEDS_PARTNER}
                                    checked={teammate === PartnerSelectionChoices.NEEDS_PARTNER}
                                    onChange={props.addTeammate}
                                    data-testid="event-reg-form.partner-selection-v2.needs-partner-radio"
                                />
                                <label htmlFor={PartnerSelectionChoices.NEEDS_PARTNER} className="flex-wrap" data-testid="event-reg-form.partner-selection-v2.needs-partner-label">
                                    <div className="radio-button-check" data-testid="event-reg-form.partner-selection-v2.needs-partner-check-icon"><Img src={'/img/icons/checkmark-icon.svg'} /></div>
                                    <div className="radio-button-label flex-1 mw0" style={{ margin: '24px 0' }} data-testid="event-reg-form.partner-selection-v2.needs-partner-description-wrapper">
                                        <div data-testid="event-reg-form.partner-selection-v2.needs-partner-title">Need Partner</div>
                                        <div className="mt8">
                                            <div className={`${styles.partnerEmailDescription} text-small mb16`} data-testid="event-reg-form.partner-selection-v2.needs-partner-description">
                                                Add yourself to a need partner list to be eligible to be paired up with another partner. Note: you could be pulled into a division other than this.
                                            </div>
                                        </div>
                                        {props.step.invite === PartnerSelectionChoices.NEEDS_PARTNER && (
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-block text-uppercase"
                                                onClick={props.addTeammateToDivision}
                                                data-testid="event-reg-form.partner-selection-v2.needs-partner-continue-btn"
                                            >
                                                Continue
                                            </button>
                                        )}
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>
                )}
                {(props.step.invite === PartnerSelectionChoices.EXISTING || props.step.invite === PartnerSelectionChoices.ADD_NEEDS_PARTNER) && props.step.selectedPlayer && (
                    <>
                        {selectedPlayer(props.step)}
                        {addPartnerError && (
                            <div className="text-center text-danger mb12 mt12">{props.addPartnerError}</div>
                        )}
                        <button
                            type="button"
                            className={`btn btn-primary btn-xl btn-block text-uppercase ${props.step.selectedPlayer ? 'mb8' : 'mb24'}`}
                            onClick={props.addTeammateToDivision}
                            disabled={!props.step.selectedPlayer || (props.step.selectedPlayer.profileId <= 0 && !props.step.selectedPlayer.email)}
                            data-testid="event-reg-form.partner-selection-v2.add-selected-partner-btn"
                        >
                            Add Partner
                        </button>
                        <button
                            type="button"
                            className="mb24 btn btn-default btn-xl btn-block text-uppercase"
                            onClick={() => props.toStep(regStepIds.TEAMMATE, props.step.divisionId, props.step.changePartnerFlow)}
                            data-testid="event-reg-form.partner-selection-v2.change-partner-btn"
                        >
                            Change Partner
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

PartnerSelectionV2.propTypes = {
    event: eventShape.isRequired,
    user: userShape.isRequired,
    step: wizardStepShape.isRequired,
    selectedNeedsPartnerPlayers: PropTypes.objectOf(playerShape).isRequired,
    selectedPlayer: PropTypes.func.isRequired,
    addPartnerError: PropTypes.string,
    addTeammate: PropTypes.func,
    selectTeammate: PropTypes.func,
    addTeammateToDivision: PropTypes.func,
    handleInviteChange: PropTypes.func,
    toStep: PropTypes.func,
    sidebarFlow: PropTypes.string,
    isNeedPartnerSelectionEnabled: PropTypes.bool,
};

PartnerSelectionV2.defaultProps = {
    addPartnerError: '',
    addTeammate: () => { },
    addTeammateToDivision: () => { },
    handleInviteChange: () => { },
    toStep: () => { },
    selectTeammate: () => { },
    sidebarFlow: '',
    isNeedPartnerSelectionEnabled: false,
};

export default PartnerSelectionV2;
